import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent } from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../../../providers/AppProvider';
import { Card } from 'react-bootstrap';
import React from 'react';

echarts.use([TooltipComponent, GridComponent, BarChart, CanvasRenderer]);

// Define the type for a single data item (Fairness Score optional — API may omit)
// Supports both LLM_Fairness_Metric ({ Action, 'Fairness Score' }) and Drift ({ Event_Index, Fairness_Score })
export interface FairnessChartLLMDataItem {
  Action?: string;
  'Fairness Score'?: number;
  Event_Index?: number;
  Fairness_Score?: number;
  'Response Sample'?: string;
}

// Define the props for the component
interface FairnessChartLLMProps {
  data: FairnessChartLLMDataItem[]; // Array of data items
  title: string;
  description: string;
  /** Chart canvas height in px (default 400). Use ~220 for compact / two-column layouts. */
  chartHeight?: number;
}

const FairnessChartLLM: React.FC<FairnessChartLLMProps> = ({ data, title, description, chartHeight = 400 }) => {
  const { getThemeColor } = useAppContext();

  // Ensure we have valid colors with fallbacks
  const primaryColor = getThemeColor('primary') || '#0d6efd';
  const bodyHighlightBg = getThemeColor('body-highlight-bg') || '#ffffff';
  const borderColor = getThemeColor('border-color') || '#dee2e6';
  const secondaryColor = getThemeColor('secondary-color') || '#6c757d';
  const lightTextEmphasis = getThemeColor('light-text-emphasis') || '#495057';

  const safeNum = (v: unknown): number => {
    const n = Number(v);
    return typeof n === 'number' && !Number.isNaN(n) && isFinite(n) ? n : 0;
  };
  // Support multiple API key variants for the score
  const getScore = (item: Record<string, unknown>): number => {
    const v = item['Fairness Score'] ?? item['fairness_score'] ?? item['fairnessScore'] ?? item['Fairness_Score'];
    return safeNum(v);
  };
  const hasData = Array.isArray(data) && data.length > 0;
  // Extract x-axis labels — when all Actions are identical, use "Event 1", "Event 2" etc. for clarity
  const rawLabels = (data || []).map(
    item => String(item.Action ?? (item as Record<string, unknown>).action ?? '').trim() || 'Event'
  );
  const uniqueActions = [...new Set(rawLabels)];
  const attributes =
    uniqueActions.length === 1 && rawLabels.length > 1 ? rawLabels.map((_, i) => `Event ${i + 1}`) : rawLabels;
  const fairnessScore = (data || []).map(item => getScore(item as Record<string, unknown>));

  // Chart options (empty placeholder when no data so ECharts still renders)
  const options = {
    color: primaryColor,
    tooltip: {
      trigger: 'axis',
      backgroundColor: bodyHighlightBg,
      borderColor: borderColor,
      textStyle: { color: lightTextEmphasis },
      borderWidth: 1,
      formatter: (params: Array<{ name: string; value: number }>) => {
        const param = params[0];
        return `<strong>${param.name}:</strong> ${param.value}`;
      }
    },
    xAxis: {
      type: 'category',
      data: attributes,
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: {
        color: secondaryColor,
        rotate: chartHeight <= 280 ? 30 : 45,
        fontSize: chartHeight <= 280 ? 10 : 12
      }
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 1,
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: { color: secondaryColor }
    },
    series: [
      {
        type: 'bar',
        data: fairnessScore,
        barWidth: '50%',
        itemStyle: {
          borderRadius: [5, 5, 0, 0],
          color: primaryColor
        }
      }
    ],
    grid: {
      left: chartHeight <= 280 ? '12%' : '10%',
      right: chartHeight <= 280 ? '8%' : '10%',
      bottom: chartHeight <= 280 ? '22%' : '15%',
      top: chartHeight <= 280 ? '12%' : '15%'
    }
  };

  const compact = chartHeight <= 280;
  const titleClass = compact ? 'h6 mb-1 fw-semibold' : 'h4 mb-1';
  const descClass = compact ? 'small text-body-secondary mb-0' : 'h6 text-body-tertiary mb-0';
  const chartWrapClass = compact
    ? 'd-flex justify-content-center px-2 pt-2 pb-1'
    : 'd-flex justify-content-center px-4 py-4';

  return (
    <>
      <Card className="h-100 border-0 shadow-sm rounded-0">
        <Card.Body className={compact ? 'p-2' : 'p-3'}>
          <div className="d-flex justify-content-between">
            <div>
              <div className={titleClass}>{title}</div>
              <div className={descClass}>{description}</div>
            </div>
          </div>
          {hasData ? (
            <div className={chartWrapClass}>
              <ReactEChartsCore
                echarts={echarts}
                option={options}
                style={{ height: `${chartHeight}px`, width: '100%' }}
              />
            </div>
          ) : (
            <div
              className={`d-flex align-items-center justify-content-center text-body-tertiary ${
                compact ? 'px-2 py-3' : 'px-4 py-6'
              }`}
              style={{ minHeight: compact ? chartHeight : undefined }}
            >
              <span className={compact ? 'small' : undefined}>
                No LLM fairness metrics were reported for this project.
              </span>
            </div>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default FairnessChartLLM;
